import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { guideFor } from '@/domain/dialGuide'
import { FrameRow } from '@/components/SampleFrames'
import { DEFAULT_LENS, type Lens } from '@/domain/lens'
import type { ControlId } from '@/domain/types'

/**
 * The deep-dive bottom sheet for one control. Scrollable, dismissed by the
 * close button, the backdrop, or Escape.
 */
export function LearnMoreSheet({
  control,
  controlName,
  lens = DEFAULT_LENS,
  onClose,
}: {
  control: ControlId
  controlName: string
  lens?: Lens
  onClose: () => void
}) {
  const guide = guideFor(control, lens)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 })
  }, [control])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-30" role="dialog" aria-modal="true" aria-label={`About the ${controlName}`}>
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/70 backdrop-blur-[2px]"
      />
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[85dvh] w-full max-w-2xl flex-col rounded-t-2xl border border-edge bg-shell shadow-2xl">
        <div className="flex items-center justify-between border-b border-edge px-4 py-3">
          <h2 className="text-[13px] font-bold uppercase tracking-[.14em]">{controlName}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted transition-colors hover:bg-edge/60 hover:text-chrome"
          >
            <X size={16} />
          </button>
        </div>

        <div ref={bodyRef} className="overflow-y-auto px-4 py-4 safe-bottom">
          {guide.examples && (
            <section className="mb-5">
              <p className="mb-2.5 text-[12.5px] leading-snug text-muted">{guide.examples.intro}</p>
              <FrameRow variants={guide.examples.variants} />
            </section>
          )}

          {guide.scale && (
            <section className="mb-5">
              <h3 className="mb-2 text-[13px] font-semibold text-chrome">{guide.scale.heading}</h3>
              <div className="overflow-hidden rounded-xl border border-edge">
                {guide.scale.rows.map((r, i) => (
                  <div
                    key={r.value}
                    className={`flex items-baseline gap-2.5 px-3 py-2 ${
                      i % 2 ? 'bg-ink/30' : 'bg-ink/55'
                    }`}
                  >
                    <span
                      className="w-[4.6rem] shrink-0 text-[12px] font-bold leading-snug text-chrome"
                      style={{ fontFamily: 'var(--font-dial)' }}
                    >
                      {r.value}
                    </span>
                    <span
                      aria-hidden="true"
                      className={`w-3 shrink-0 text-center text-[11px] font-bold ${
                        r.tone === 'brighter'
                          ? 'text-amber'
                          : r.tone === 'darker'
                            ? 'text-muted'
                            : 'text-muted/40'
                      }`}
                      title={
                        r.tone === 'brighter'
                          ? 'brighter'
                          : r.tone === 'darker'
                            ? 'darker'
                            : 'no brightness change'
                      }
                    >
                      {r.tone === 'brighter' ? '▲' : r.tone === 'darker' ? '▼' : '·'}
                    </span>
                    <span className="text-[12.5px] leading-snug text-muted">{r.effect}</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[12.5px] leading-relaxed text-muted">{guide.scale.note}</p>
            </section>
          )}

          {guide.sections.map((s) => (
            <section key={s.heading} className="mb-4">
              <h3 className="mb-1 text-[13px] font-semibold text-chrome">{s.heading}</h3>
              <p className="text-[13px] leading-relaxed text-muted">{s.body}</p>
            </section>
          ))}

          <p className="mt-5 border-t border-edge pt-3 text-[10.5px] leading-relaxed text-muted/70">
            Dial behaviour follows the Nikon Z fc Reference Guide. Sample frames are simulated so
            each row shows the identical scene with only one setting changed.
          </p>
        </div>
      </div>
    </div>
  )
}
